import React, { useState, useEffect } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import {
  Menu,
  X,
  HomeIcon,
  Tag,
  MessageCircle,
  Users,
  Gamepad2,
  ShieldAlert,
  ChevronDown,
  ChevronRight,
  Moon,
  Sun,
  User,
} from 'lucide-react';
import useHeader from '../../hooks/useHeader';
import useUserContext from '../../hooks/useUserContext';
import useThemeToggle from '../../hooks/useThemeToggle';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import cn from '../../lib/utils';

/**
 * Header component that renders the main title and a search bar.
 * The search bar allows the user to input a query and navigate to the search results page
 * when they press Enter.
 */
const Header = () => {
  const { handleSignOut } = useHeader();
  const { user: currentUser } = useUserContext();
  const { isDark, handleToggleTheme, themeRefreshHandler } = useThemeToggle();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showMessagingOptions, setShowMessagingOptions] = useState(false);

  const [selectedModel, setSelectedModel] = useState('gpt-4o-mini');

  const [profilePicture, setProfilePicture] = useState<string | undefined>(
    currentUser.profilePicture,
  );

  // Apply theme on component mount
  useEffect(() => {
    themeRefreshHandler();
  }, [themeRefreshHandler]);

  // Check session storage for updated profile picture
  useEffect(() => {
    const checkForProfileUpdates = () => {
      const storedUser = sessionStorage.getItem('currentUser');
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser.profilePicture !== profilePicture) {
            setProfilePicture(parsedUser.profilePicture);
          }
        } catch (error) {
          // silently handle error
        }
      }
    };

    // Check initially and then every 2 seconds
    checkForProfileUpdates();
    const interval = setInterval(checkForProfileUpdates, 2000);

    return () => clearInterval(interval);
  }, [profilePicture]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleMessagingOptions = () => {
    setShowMessagingOptions(!showMessagingOptions);
  };

  const isActiveLink = (path: string): boolean => window.location.pathname === path;

  const handleModelChange = async (value: string) => {
    setSelectedModel(value);
    // try {
    //   await fetch('/api/config/update-model', {
    //     method: 'POST',
    //     headers: {
    //       'Content-Type': 'application/json',
    //     },
    //     body: JSON.stringify({ model: value }),
    //   });
    // } catch (error) {
    //   console.error('Failed to update model:', error);
    // }
  };

  const viewProfile = () => {
    navigate(`/user/${currentUser.username}`);
    setMobileMenuOpen(false);
  };

  return (
    <header className='sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60'>
      <div className='container flex h-14 items-center justify-between'>
        <div className='flex items-center space-x-4'>
          <NavLink to='/home' className='flex items-center'>
            <img src='/syntax-error-logo.png' alt='Syntax Error' className='h-7 w-auto mr-2' />
            <span className='text-xl font-semibold'>Syntax Error</span>
          </NavLink>
        </div>

        {/* Desktop navigation */}
        <div className='hidden md:flex items-center space-x-4'>
          <Button variant='ghost' size='icon' onClick={handleToggleTheme} title='Toggle theme'>
            {isDark ? <Sun size={20} /> : <Moon size={20} />}
          </Button>
          <Select value={selectedModel} onValueChange={handleModelChange}>
            <SelectTrigger className='w-[180px]'>
              <SelectValue placeholder='Select AI Model' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='gpt-4o-mini'>GPT-4o Mini</SelectItem>
              <SelectItem value='gpt-3.5-turbo'>GPT-3.5 Turbo</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant='ghost'
            disabled={currentUser.username.startsWith('Guest_')}
            onClick={() => navigate(`/user/${currentUser.username}`)}>
            View Profile
          </Button>

          {/* Profile button with avatar */}
          <div className='flex items-center'>
            <button
              onClick={viewProfile}
              disabled={currentUser.username.startsWith('Guest_')}
              className='flex items-center space-x-2 hover:bg-gray-100 dark:hover:bg-gray-800 p-2 rounded-full transition'>
              {profilePicture ? (
                <img
                  src={profilePicture}
                  alt='Profile'
                  className='h-8 w-8 rounded-full object-cover border border-gray-200 dark:border-gray-700'
                />
              ) : (
                <div className='h-8 w-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center border border-gray-200 dark:border-gray-700'>
                  <User className='h-4 w-4 text-gray-500 dark:text-gray-400' />
                </div>
              )}
              <span
                className={`text-sm ${currentUser.username.startsWith('Guest_') ? 'text-gray-400' : ''}`}>
                {currentUser.username.length > 12
                  ? `${currentUser.username.substring(0, 10)}...`
                  : currentUser.username}
              </span>
            </button>
          </div>

          <Button variant='outline' onClick={handleSignOut}>
            Log out
          </Button>
        </div>

        {/* Mobile menu button */}
        <div className='md:hidden'>
          <Button variant='ghost' size='sm' onClick={toggleMobileMenu}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className='md:hidden container pb-4'>
          {/* Navigation links */}
          <div className='flex flex-col space-y-2 mb-4'>
            <NavLink
              to='/home'
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                cn(
                  'flex items-center px-3 py-2 rounded-md transition-colors',
                  isActive ? 'bg-gray-200 text-blue-700' : 'text-blue-500 hover:bg-gray-100',
                )
              }>
              <HomeIcon size={18} className='mr-2' />
              <span>Questions</span>
            </NavLink>

            <NavLink
              to='/tags'
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                cn(
                  'flex items-center px-3 py-2 rounded-md transition-colors',
                  isActive ? 'bg-gray-200 text-blue-700' : 'text-blue-500 hover:bg-gray-100',
                )
              }>
              <Tag size={18} className='mr-2' />
              <span>Tags</span>
            </NavLink>

            {/* Messaging with dropdown */}
            <div>
              <div
                className={cn(
                  'flex items-center justify-between px-3 py-2 rounded-md transition-colors cursor-pointer',
                  isActiveLink('/messaging') || isActiveLink('/messaging/direct-message')
                    ? 'bg-gray-200 text-blue-700'
                    : 'text-blue-500 hover:bg-gray-100',
                )}
                onClick={toggleMessagingOptions}>
                <div className='flex items-center'>
                  <MessageCircle size={18} className='mr-2' />
                  <span>Messaging</span>
                </div>
                {showMessagingOptions ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </div>

              {showMessagingOptions && (
                <div className='ml-6 mt-1 space-y-1'>
                  <NavLink
                    to='/messaging'
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center px-3 py-2 rounded-md transition-colors',
                        isActive ? 'bg-blue-500 text-white' : 'text-blue-500 hover:bg-gray-100',
                      )
                    }>
                    <span>Global Messages</span>
                  </NavLink>
                  <NavLink
                    to='/messaging/direct-message'
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center px-3 py-2 rounded-md transition-colors',
                        isActive ? 'bg-blue-500 text-white' : 'text-blue-500 hover:bg-gray-100',
                      )
                    }>
                    <span>Direct Messages</span>
                  </NavLink>
                </div>
              )}
            </div>

            <NavLink
              to='/users'
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                cn(
                  'flex items-center px-3 py-2 rounded-md transition-colors',
                  isActive ? 'bg-gray-200 text-blue-700' : 'text-blue-500 hover:bg-gray-100',
                )
              }>
              <Users size={18} className='mr-2' />
              <span>Users</span>
            </NavLink>

            <NavLink
              to='/games'
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                cn(
                  'flex items-center px-3 py-2 rounded-md transition-colors',
                  isActive ? 'bg-gray-200 text-blue-700' : 'text-blue-500 hover:bg-gray-100',
                )
              }>
              <Gamepad2 size={18} className='mr-2' />
              <span>Games</span>
            </NavLink>

            {currentUser.isAdmin && (
              <NavLink
                to='/admin'
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center px-3 py-2 rounded-md transition-colors',
                    isActive ? 'bg-red-100 text-red-700' : 'text-red-600 hover:bg-red-50',
                  )
                }>
                <ShieldAlert size={18} className='mr-2' />
                <span>Admin Dashboard</span>
              </NavLink>
            )}
          </div>

          {/* User actions */}
          <div className='border-t pt-2 flex flex-col space-y-2'>
            <Button
              variant='ghost'
              className='flex items-center justify-center'
              onClick={handleToggleTheme}>
              <span className='mr-2'>{isDark ? <Sun size={18} /> : <Moon size={18} />}</span>
              {isDark ? 'Light Mode' : 'Dark Mode'}
            </Button>

            {/* Profile button with avatar for mobile */}
            <Button
              variant='ghost'
              disabled={currentUser.username.startsWith('Guest_')}
              onClick={viewProfile}
              className='flex justify-start items-center space-x-2'>
              {profilePicture ? (
                <img
                  src={profilePicture}
                  alt='Profile'
                  className='h-8 w-8 rounded-full object-cover border border-gray-200 dark:border-gray-700'
                />
              ) : (
                <div className='h-8 w-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center border border-gray-200 dark:border-gray-700'>
                  <User className='h-4 w-4 text-gray-500 dark:text-gray-400' />
                </div>
              )}
              <span>View Profile</span>
            </Button>

            <Button variant='outline' onClick={handleSignOut}>
              Log out
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
